/**
 * App sidebar (design OR-1537, built with OR-1664).
 *
 * Desktop (≥ lg) is a collapsible rail that sits in the page flow, so
 * opening it **pushes** the content instead of covering it — same
 * behaviour as the Gemini and Centaur sidebars, per Andressa's note:
 *
 *   collapsed (68px)            expanded (264px)
 *   ┌──────┐                    ┌───────────────────────┐
 *   │  ◉   [»]                  │ ◉ ORION           [«] │
 *   │ ──── │                    │ ───────────────────── │
 *   │  📋  │ ← tooltip label     │ 📋 Submission Triage  │
 *   │  🛡  │                    │ 🛡 Policies            │
 *   │      │                    │                       │
 *   │ ──── │                    │ ───────────────────── │
 *   │  OR  │ ← tooltip name      │ OR  Olivia Rhye     ⌄ │
 *   └──────┘                    └───────────────────────┘
 *
 * The toggle straddles the rail's right edge when collapsed and sits
 * inside it when expanded; its tooltip reads "Open" / "Close". The
 * choice is remembered per browser.
 *
 * Mobile (< lg) keeps atlas' header + drawer from `SidebarNavigationSlim`:
 * its desktop half is hidden by the `lg:hidden` wrapper, so only the
 * mobile header renders.
 *
 * Navigation goes through the router (`<Link>`), so moving between
 * sections is a client-side transition — no full page reload.
 */
import { type FC, useCallback, useState } from "react";
import { Button as AriaButton, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover, Pressable } from "react-aria-components";
import { ChevronDown, ChevronLeftDouble, ChevronRightDouble, Clipboard, Shield01 } from "@untitledui/icons";
import { Link, useLocation } from "react-router";
import { Avatar, BrandLogo, type NavItemType, SidebarNavigationSlim, Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";
import { useAuth } from "../auth";
import { AccountMenu } from "./account-menu";

type Item = NavItemType & { icon: FC<{ className?: string }>; href: string; "data-autotest": string };

const items: Item[] = [
    { label: "Submission Triage", href: "/submissions", icon: Clipboard, "data-autotest": "app-sidebar-button-submissions" },
    // OR-1664: the Policies list. Same tab behaviour as Submission Triage.
    { label: "Policies", href: "/policies", icon: Shield01, "data-autotest": "app-sidebar-button-policies" },
];

const COLLAPSED_WIDTH = 68;
const EXPANDED_WIDTH = 264;
/** Toggle button size (size-7). */
const TOGGLE_SIZE = 28;
/** How far the collapsed toggle reaches back into the rail — the rest
 *  overhangs the content, as in the design. */
const TOGGLE_OVERLAP = 9;
const STORAGE_KEY = "orion.sidebar.expanded";

const getInitials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");

/** The nav item whose section the current URL falls under, so the rail
 *  highlights Policies on `/policies` and Triage everywhere under
 *  `/submissions` (including a submission's own pages). */
function activeHrefFor(pathname: string): string {
    const match = items.find((item) => pathname === item.href || pathname.startsWith(`${item.href}/`));
    return match?.href ?? "/submissions";
}

/** Local-part of an email (`gustavo@orion.io` → `gustavo`). Used as a
 *  graceful fallback when the backend hasn't sent a `name` for the user
 *  — we'd rather render `gustavo` on top of the full email than echo
 *  the same address on both lines. */
const usernameFromEmail = (e?: string) => (e ? e.split("@")[0] : "");

/** Collapsed by default; the user's last choice wins after that. Storage
 *  can be unavailable (private mode), which just means it isn't kept. */
function useExpanded(): [boolean, () => void] {
    const [expanded, setExpanded] = useState(() => {
        try {
            return window.localStorage.getItem(STORAGE_KEY) === "true";
        } catch {
            return false;
        }
    });
    const toggle = useCallback(() => {
        setExpanded((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(STORAGE_KEY, String(next));
            } catch {
                // ignore
            }
            return next;
        });
    }, []);
    return [expanded, toggle];
}

/** Logo + "ORION" wordmark, as the expanded rail and the mobile header
 *  show it. The wordmark is set in type (no exported asset yet), matched
 *  to the design: light, wide-tracked, in the brand accent. */
function OrionWordmark() {
    return (
        <span className="inline-flex items-center gap-2" aria-label="Orion">
            <BrandLogo className="size-8 shrink-0" alt="" />
            <span aria-hidden="true" className="text-brand-tertiary text-lg font-light tracking-[0.3em]">
                ORION
            </span>
        </span>
    );
}

export function OrionSidebar() {
    const { user, logout } = useAuth();
    const { pathname } = useLocation();
    const [expanded, toggle] = useExpanded();
    // The sidebar mounts under <ProtectedRoute>, so `user` is non-null
    // by the time we render — but we keep a defensive fallback so a
    // brief bootstrap window never blanks the avatar.
    const displayName =
        user?.name ||
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        usernameFromEmail(user?.email) ||
        "—";
    const email = user?.email ?? "";
    const activeHref = activeHrefFor(pathname);
    const avatar = (
        <Avatar border initials={getInitials(displayName)} size="md" alt={displayName} src={user?.avatarUrl} data-autotest="app-sidebar-avatar-user-profile" />
    );
    const accountMenu = <AccountMenu onSignOut={logout} />;
    const width = expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

    return (
        <>
            {/* ---------- Desktop rail (≥ lg) ---------- */}
            <aside
                data-autotest="app-sidebar-navigation"
                data-expanded={expanded}
                style={{ width }}
                className="border-secondary bg-primary relative z-40 hidden h-screen shrink-0 flex-col border-r transition-[width] duration-200 ease-out lg:sticky lg:top-0 lg:flex"
            >
                <div className={cx("flex h-8 items-center pt-5 box-content", expanded ? "px-5" : "justify-center")}>
                    {expanded ? <OrionWordmark /> : <BrandLogo className="size-8" />}
                </div>

                <Tooltip title={expanded ? "Close" : "Open"} placement="right" arrow={false}>
                    <TooltipTrigger
                        onPress={toggle}
                        aria-label={expanded ? "Close sidebar" : "Open sidebar"}
                        aria-expanded={expanded}
                        data-autotest="app-sidebar-button-toggle"
                        style={{
                            width: TOGGLE_SIZE,
                            height: TOGGLE_SIZE,
                            top: 20 + (32 - TOGGLE_SIZE) / 2,
                            left: expanded ? width - 20 - TOGGLE_SIZE : width - TOGGLE_OVERLAP,
                        }}
                        className={({ isFocusVisible }) =>
                            cx(
                                "border-secondary bg-primary hover:bg-primary_hover absolute! inline-flex! cursor-pointer items-center justify-center rounded-md border shadow-xs transition-[left] duration-200 ease-out",
                                isFocusVisible && "outline-focus-ring outline-2 outline-offset-2",
                            )
                        }
                    >
                        {expanded ? (
                            <ChevronLeftDouble aria-hidden="true" className="text-fg-quaternary size-4" />
                        ) : (
                            <ChevronRightDouble aria-hidden="true" className="text-fg-quaternary size-4" />
                        )}
                    </TooltipTrigger>
                </Tooltip>

                <div className={cx("border-secondary mt-5 border-t", expanded ? "mx-4" : "mx-3")} />

                <nav aria-label="Main">
                    <ul className={cx("mt-4 flex flex-col gap-1", expanded ? "px-4" : "items-center px-3")}>
                        {items.map((item) => (
                            <li key={item.href} className={expanded ? undefined : "w-full"}>
                                <NavLinkItem item={item} current={item.href === activeHref} expanded={expanded} />
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className={cx("border-secondary mt-auto border-t py-4", expanded ? "mx-4" : "mx-3 flex justify-center")}>
                    <AriaDialogTrigger>
                        {expanded ? (
                            <AriaButton
                                data-autotest="app-sidebar-button-account"
                                className={({ isFocusVisible }) =>
                                    cx(
                                        "hover:bg-primary_hover flex w-full cursor-pointer items-center gap-3 rounded-md px-1 py-1.5 text-left",
                                        isFocusVisible && "outline-focus-ring outline-2 outline-offset-2",
                                    )
                                }
                            >
                                {avatar}
                                <span className="text-secondary min-w-0 flex-1 truncate text-sm font-semibold" title={email || undefined}>
                                    {displayName}
                                </span>
                                <ChevronDown aria-hidden="true" className="text-fg-quaternary size-4 shrink-0" />
                            </AriaButton>
                        ) : (
                            <Tooltip title={displayName} placement="right" arrow={false}>
                                <AriaButton
                                    aria-label={displayName}
                                    data-autotest="app-sidebar-button-account"
                                    className={({ isFocusVisible }) =>
                                        cx("cursor-pointer rounded-full", isFocusVisible && "outline-focus-ring outline-2 outline-offset-2")
                                    }
                                >
                                    {avatar}
                                </AriaButton>
                            </Tooltip>
                        )}
                        <AriaPopover
                            placement="right bottom"
                            offset={8}
                            className={({ isEntering, isExiting }) =>
                                cx(
                                    "will-change-transform",
                                    isEntering && "animate-in fade-in placement-right:slide-in-from-left-2 duration-300 ease-out",
                                    isExiting && "animate-out fade-out placement-right:slide-out-to-left-2 duration-150 ease-in",
                                )
                            }
                        >
                            {accountMenu}
                        </AriaPopover>
                    </AriaDialogTrigger>
                </div>
            </aside>

            {/* ---------- Mobile header + drawer (< lg) ---------- */}
            <div className="lg:hidden">
                <SidebarNavigationSlim
                    flush
                    items={items}
                    activeUrl={activeHref}
                    logo={<BrandLogo />}
                    logoMobile={<OrionWordmark />}
                    avatar={avatar}
                    userName={displayName}
                    userEmail={email}
                    accountMenu={accountMenu}
                />
            </div>
        </>
    );
}

/** One nav entry: icon-only with a tooltip when collapsed, icon + label
 *  when expanded. */
function NavLinkItem({ item, current, expanded }: { item: Item; current: boolean; expanded: boolean }) {
    const Icon = item.icon;
    const className = cx(
        "group/item flex items-center rounded-md transition duration-100 ease-linear outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
        current ? "bg-secondary hover:bg-secondary_hover" : "hover:bg-primary_hover",
        expanded ? "h-9 w-full gap-2 px-2" : "mx-auto size-9 justify-center",
    );
    const icon = (
        <Icon
            aria-hidden="true"
            className={cx("text-fg-quaternary group-hover/item:text-fg-quaternary_hover size-5 shrink-0", current && "text-fg-quaternary_hover")}
        />
    );

    if (expanded) {
        return (
            <Link to={item.href} aria-current={current ? "page" : undefined} data-autotest={item["data-autotest"]} className={className}>
                {icon}
                {/* Figma "_Nav item base": Text sm/Medium (500), text-tertiary when
                  * not selected. */}
                <span
                    className={cx(
                        "truncate text-sm font-medium",
                        current ? "text-secondary_hover" : "text-tertiary group-hover/item:text-tertiary_hover",
                    )}
                >
                    {item.label}
                </span>
            </Link>
        );
    }

    return (
        // Same pattern as atlas' NavButton: `Pressable` makes the link
        // itself the tooltip trigger, so there is no button around an <a>.
        <Tooltip title={item.label} placement="right" arrow={false}>
            <Pressable>
                <Link
                    to={item.href}
                    aria-label={item.label}
                    aria-current={current ? "page" : undefined}
                    data-autotest={item["data-autotest"]}
                    className={className}
                >
                    {icon}
                </Link>
            </Pressable>
        </Tooltip>
    );
}
